import { redirect } from '@tanstack/react-router'
import { hasAdminOrAvaliadorAccessFromToken, isAdminFromToken, isAvaliadorFromToken, getUserRolesFromToken } from './token-helpers'
import type { Role } from '@/components/layout/types'

const EMPLOYEE_DEFAULT_ROUTE = '/novo-documento' as const
const ADMIN_DEFAULT_ROUTE = '/documentos' as const
const BC_DEFAULT_ROUTE = '/background-check' as const
const HELP_FAQ_ROUTE = '/ajuda-faq' as const

export const ROUTES = {
  EMPLOYEE_DEFAULT: EMPLOYEE_DEFAULT_ROUTE,
  ADMIN_DEFAULT: ADMIN_DEFAULT_ROUTE,
  BC_DEFAULT: BC_DEFAULT_ROUTE,
  HELP_FAQ: HELP_FAQ_ROUTE,
} as const

/**
 * Verifica se o usuário atual tem acesso de admin ou avaliador através do token.
 * Retorna true se o usuário for admin ou avaliador.
 * Usa o token JWT diretamente, igual ao backend.
 */
export async function hasAdminOrAvaliadorAccess(): Promise<boolean> {
  return hasAdminOrAvaliadorAccessFromToken()
}

/**
 * Verifica se o usuário atual tem role de admin ou avaliador através do token.
 * Se não tiver, redireciona para a rota padrão de funcionários.
 *
 * @throws {Redirect} Redireciona para /novo-documento se não for admin ou avaliador
 */
export async function requireAdminOrAvaliador(): Promise<void> {
  const hasAccess = await hasAdminOrAvaliadorAccessFromToken()
  if (!hasAccess) {
    throw redirect({
      to: EMPLOYEE_DEFAULT_ROUTE,
      replace: true,
    })
  }
}

/**
 * Verifica se o usuário atual tem role de admin através do token.
 * Se não tiver, redireciona para a rota padrão de funcionários.
 *
 * @throws {Redirect} Redireciona para /novo-documento se não for admin
 */
export async function requireAdmin(): Promise<void> {
  const isAdmin = await isAdminFromToken()
  if (!isAdmin) {
    throw redirect({
      to: EMPLOYEE_DEFAULT_ROUTE,
      replace: true,
    })
  }
}

/**
 * Verifica se o usuário atual tem role de avaliador através do token.
 * Se não tiver, redireciona para a rota padrão de funcionários.
 *
 * @throws {Redirect} Redireciona para /novo-documento se não for avaliador
 */
export async function requireAvaliador(): Promise<void> {
  const isAvaliador = await isAvaliadorFromToken()
  if (!isAvaliador) {
    throw redirect({
      to: EMPLOYEE_DEFAULT_ROUTE,
      replace: true,
    })
  }
}

/** Any role that can access Background Check module. */
export async function requireBcAccess(): Promise<void> {
  const roles = await getUserRolesFromToken()
  const bcRoles = ['admin', 'avaliador', 'admin-background-check', 'avaliador-background-check']
  const hasAccess = roles.some((r: string) => bcRoles.includes(r))
  if (!hasAccess) {
    throw redirect({ to: EMPLOYEE_DEFAULT_ROUTE, replace: true })
  }
}

/** Admin roles that can access BC admin features (uso, auditoria). */
export async function requireBcAdmin(): Promise<void> {
  const roles = await getUserRolesFromToken()
  const adminRoles = ['admin', 'admin-background-check']
  const hasAccess = roles.some((r: string) => adminRoles.includes(r))
  if (!hasAccess) {
    throw redirect({ to: EMPLOYEE_DEFAULT_ROUTE, replace: true })
  }
}

/** True when user has ONLY BC roles (no admin/avaliador for Atestados). */
export async function isBcOnlyUser(): Promise<boolean> {
  const roles = await getUserRolesFromToken()
  const atestadosRoles = ['admin', 'avaliador']
  return roles.length > 0 && !roles.some((r: string) => atestadosRoles.includes(r))
}

/** Block BC-only users from Atestados routes. */
export async function requireAtestadosAccess(): Promise<void> {
  const bcOnly = await isBcOnlyUser()
  if (bcOnly) {
    throw redirect({ to: BC_DEFAULT_ROUTE, replace: true })
  }
}

export async function requireProduct(
  _produto: string,
  allowedRoles: Role[],
): Promise<void> {
  const isAdminUser = await isAdminFromToken()
  const isAvaliadorUser = await isAvaliadorFromToken()

  let currentRole: Role = 'employee'
  if (isAdminUser) currentRole = 'admin'
  else if (isAvaliadorUser) currentRole = 'avaliador'

  if (!allowedRoles.includes(currentRole)) {
    throw redirect({
      to: '/sem-permissao' as string,
      replace: true,
    })
  }
}

